// The yearbook (scenario H9's league half, PLAN-2026-10-31-the-yearbook): the
// league's season told once its champion is crowned — the champion, the class
// (every team and the one thing it is remembered for), the league records the
// season set, the games to remember and the moves that paid. Pure: the awards
// engine's facts and the minted awards in, the yearbook out. No clocks, no
// I/O, no sport names.
//
// THIRD PERSON, BY TEAM NAME, as the wrap is (`wrap.ts`): the class photo is
// made to be passed around.

import {
  DEFAULT_CATALOG,
  gamesOf,
  type IconId,
  type MatchupFact,
  type Minted,
  type SeasonFacts,
  type Tone,
} from '@fsp/awards';
import type { WrapHow, WrapShare } from './wrap';
import { listOf, possessive, pts, recordOf, times, winnerOf } from './season-words';

export interface YearbookHonor {
  awardId: string;
  name: string;
  icon: IconId;
  tone: Tone;
}

/** One team in the class: how it ended, and what it is remembered for. */
export interface YearbookTeam {
  teamId: string;
  how: WrapHow;
  /** Its place in the regular season; null for a team the table does not have. */
  rank: number | null;
  /** "10–4", or its points in a league without results. */
  record: string | null;
  /** Every season award it won, the glory first. */
  honors: YearbookHonor[];
  /** How many weekly awards it won. */
  weekly: number;
  /** The one thing it is remembered for: "Champion", "Benchwarmer", "High Score ×3", "Best week 131.2". */
  tag: string;
  tone: Tone;
  icon: IconId | null;
  line: string;
}

export type YearbookRecordId = 'high-week' | 'blowout' | 'streak' | 'season-points';

export interface YearbookRecord {
  id: YearbookRecordId;
  name: string;
  value: string;
  teamId: string;
  when: string;
  line: string;
}

export type YearbookGameId = 'closest' | 'highest' | 'blowout' | 'upset';

export interface YearbookGame {
  id: YearbookGameId;
  title: string;
  /** The game's figure: the margin, the total; none for the upset. */
  value: string | null;
  line: string;
  matchupId: string;
  period: number;
  homeTeamId: string;
  awayTeamId: string;
  homeScore: number;
  awayScore: number;
  /** Who it went to, as the scorer settled it; null for a tie. */
  winnerTeamId: string | null;
}

export interface YearbookMove {
  id: 'pickup' | 'trade';
  title: string;
  value: string;
  line: string;
  teamId: string;
  playerId: string;
}

export interface Yearbook {
  over: boolean;
  champion: {
    teamId: string;
    runnerUpTeamId: string | null;
    /** "2026 League Champion": the trophy's name, a title worn. */
    title: string;
    line: string;
    /** The final, its last leg; null in a league with no bracket. */
    matchupId: string | null;
  } | null;
  teams: YearbookTeam[];
  records: {
    /** No past season kept any of the four: this season's bests are the records. */
    first: boolean;
    line: string;
    set: YearbookRecord[];
  };
  games: YearbookGame[];
  moves: {
    pickups: number;
    traded: number;
    line: string;
    items: YearbookMove[];
  };
  share: WrapShare | null;
}

export interface YearbookInput {
  facts: SeasonFacts;
  /** This season's awards as handed out; a superseded one is left out here. */
  mints: readonly Minted[];
  /** "Week 7", "Semifinal": the league's own name for a week. */
  periodLabel: (period: number) => string;
}

const NOT_OVER: Yearbook = {
  over: false,
  champion: null,
  teams: [],
  records: { first: false, line: '', set: [] },
  games: [],
  moves: { pickups: 0, traded: 0, line: '', items: [] },
  share: null,
};

const TONE_ORDER: Record<Tone, number> = { glory: 0, fun: 1, shame: 2 };
const CATALOG_ORDER = new Map(DEFAULT_CATALOG.awards.map((a, i) => [a.id, i]));

/** The glory first, then the fun, then the shame; each tone in the catalog's order. */
function byTone(a: { tone: Tone; awardId: string }, b: { tone: Tone; awardId: string }): number {
  return (
    TONE_ORDER[a.tone] - TONE_ORDER[b.tone] ||
    (CATALOG_ORDER.get(a.awardId) ?? 999) - (CATALOG_ORDER.get(b.awardId) ?? 999)
  );
}

const round1 = (n: number) => Math.round(n * 10) / 10;

export function leagueYearbook(input: YearbookInput): Yearbook {
  const { facts, periodLabel } = input;
  const b = facts.bracket;
  // OVER ONCE THE CHAMPION IS DECIDED: a bracket's final played, or a league
  // with no bracket decided by its table (B-362).
  if (!b.final || b.championTeamId === null) return NOT_OVER;
  const championId = b.championTeamId;
  const nameOf = (id: string | null) =>
    (id === null ? null : facts.teams.find((t) => t.teamId === id)?.name) ?? 'a team';
  const finals = facts.periods.filter((p) => p.final);
  const games = finals.flatMap((p) => p.matchups.filter((m) => m.final));
  const mints = input.mints.filter((m) => !m.supersededBy);
  const won = (teamId: string) => mints.filter((m) => m.winners.some((w) => w.teamId === teamId));
  const noBracket = b.playoffTeamIds.length === 0;

  // ── The champion ───────────────────────────────────────────────────────
  const finalWeeks = new Set(b.finalPeriods ?? []);
  const theFinal = noBracket
    ? undefined
    : [...games]
        .filter(
          (m) =>
            m.kind === 'playoff' &&
            (finalWeeks.size === 0 || finalWeeks.has(m.period)) &&
            [m.homeTeamId, m.awayTeamId].includes(championId) &&
            (b.runnerUpTeamId === null || [m.homeTeamId, m.awayTeamId].includes(b.runnerUpTeamId)),
        )
        .sort((x, y) => y.period - x.period)[0];
  const title = `${facts.season} ${facts.trophyName}`;
  const champion = {
    teamId: championId,
    runnerUpTeamId: b.runnerUpTeamId,
    title,
    line: `${nameOf(championId)} are the ${title}. ${b.finalLine ?? ''}`.trim(),
    matchupId: theFinal?.id ?? null,
  };

  // ── The class ──────────────────────────────────────────────────────────
  const standing = new Map(facts.standings.map((s) => [s.teamId, s]));
  const order = [
    championId,
    ...(b.runnerUpTeamId !== null && b.runnerUpTeamId !== championId ? [b.runnerUpTeamId] : []),
    ...[...facts.standings].sort((x, y) => x.rank - y.rank).map((s) => s.teamId),
    ...[...facts.teams].sort((x, y) => x.name.localeCompare(y.name)).map((t) => t.teamId),
  ];
  const seen = new Set<string>();
  const known = new Set(facts.teams.map((t) => t.teamId));
  const teams: YearbookTeam[] = [];
  for (const teamId of order) {
    if (seen.has(teamId) || !known.has(teamId)) continue;
    seen.add(teamId);
    const s = standing.get(teamId);
    const played = s ? s.wins + s.losses + s.ties : 0;
    const how: WrapHow =
      teamId === championId
        ? 'champion'
        : noBracket
          ? 'finished'
          : teamId === b.runnerUpTeamId
            ? 'runner-up'
            : b.playoffTeamIds.includes(teamId)
              ? 'playoffs'
              : 'missed';
    const mine = won(teamId);
    const season = mine.filter((m) => m.scope === 'season').sort(byTone);
    const weekly = mine.filter((m) => m.scope === 'period');
    const honors = season.map((m) => ({
      awardId: m.awardId,
      name: m.name,
      icon: m.icon,
      tone: m.tone,
    }));
    const team = nameOf(teamId);
    let tag: string;
    let tone: Tone;
    let icon: IconId | null;
    let line: string;
    const top = season[0];
    if (top) {
      // The trophy's line is the final's, in the third person; the rest are
      // the award's own sentence as it was minted.
      tag = top.name;
      tone = top.tone;
      icon = top.icon;
      line = top.awardId === 'champion' && b.finalLine ? `${b.finalLine}` : top.line;
    } else if (weekly.length > 0) {
      const counts = new Map<string, { m: Minted; n: number }>();
      for (const m of weekly) {
        const row = counts.get(m.awardId) ?? { m, n: 0 };
        row.n += 1;
        counts.set(m.awardId, row);
      }
      const ranked = [...counts.values()].sort((x, y) => y.n - x.n || byTone(x.m, y.m));
      const most = ranked[0]!;
      tag = most.n > 1 ? `${most.m.name} ×${most.n}` : most.m.name;
      tone = most.m.tone;
      icon = most.m.icon;
      // THE THREE WON MOST, and how many more: a champion of the wire won
      // seven different ones, and a line naming them all ran to four.
      const named = ranked
        .slice(0, 3)
        .map((r) => (r.n > 1 ? `${r.m.name} ${times(r.n)}` : r.m.name));
      const more = ranked.length - named.length;
      line = `${weekly.length} weekly award${weekly.length === 1 ? '' : 's'}: ${listOf(
        more > 0 ? [...named, `${more} more`] : named,
      )}.`;
    } else {
      const best = finals
        .flatMap((p) => p.teams.filter((t) => t.teamId === teamId && t.played))
        .reduce<{ score: number; period: number } | null>(
          (top, t) =>
            top === null || t.score > top.score ? { score: t.score, period: t.period } : top,
          null,
        );
      tone = 'fun';
      icon = null;
      if (best !== null && best.score > 0) {
        tag = `Best week ${pts(best.score)}`;
        line = `Best week: ${pts(best.score)} in ${periodLabel(best.period)}.`;
      } else {
        tag = 'In the books';
        line = `${possessive(team)} ${facts.season} season, in the books.`;
      }
    }
    teams.push({
      teamId,
      how,
      rank: s?.rank ?? null,
      record: s
        ? played > 0
          ? recordOf(s.wins, s.losses, s.ties)
          : `${pts(s.pointsFor)} points`
        : null,
      honors,
      weekly: weekly.length,
      tag,
      tone,
      icon,
      line,
    });
  }

  // ── The records ────────────────────────────────────────────────────────
  // THIS SEASON'S BESTS BY THE RECORD BOOK'S OWN RULES (`seasonBests`): each
  // team's over every final game in week order, a week against the field a
  // best week and nothing else, a streak running across every kind of game.
  // A TIE GOES TO THE HIGHER FINISH, as the record book's walk down the
  // season's table gives it, so the two never name different holders.
  type Best = { value: number; teamId: string; when: string } | null;
  interface Own {
    week: number;
    weekAt: number;
    margin: number;
    marginAt: number;
    streak: number;
    streakFrom: number;
    streakTo: number;
    run: number;
    runFrom: number;
  }
  const own = new Map<string, Own>();
  const ownOf = (id: string): Own => {
    let o = own.get(id);
    if (!o) {
      o = {
        week: 0,
        weekAt: 0,
        margin: 0,
        marginAt: 0,
        streak: 0,
        streakFrom: 0,
        streakTo: 0,
        run: 0,
        runFrom: 0,
      };
      own.set(id, o);
    }
    return o;
  };
  for (const p of finals)
    for (const t of p.teams) {
      if (!t.played) continue;
      const o = ownOf(t.teamId);
      if (t.score > o.week) {
        o.week = t.score;
        o.weekAt = p.period;
      }
    }
  for (const m of [...games].sort((x, y) => x.period - y.period)) {
    const w = winnerOf(m);
    for (const side of [m.homeTeamId, m.awayTeamId]) {
      const o = ownOf(side);
      if (w !== side) {
        o.run = 0;
        continue;
      }
      if (o.run === 0) o.runFrom = m.period;
      o.run += 1;
      const margin = round1(Math.abs(m.homeScore - m.awayScore));
      if (margin > o.margin) {
        o.margin = margin;
        o.marginAt = m.period;
      }
      if (o.run > o.streak) {
        o.streak = o.run;
        o.streakFrom = o.runFrom;
        o.streakTo = m.period;
      }
    }
  }
  const rankOf = (id: string) => standing.get(id)?.rank ?? Number.POSITIVE_INFINITY;
  const holder = (value: (o: Own) => number, when: (o: Own) => string): Best => {
    let top: Best = null;
    for (const [teamId, o] of own) {
      const v = value(o);
      if (v <= 0) continue;
      if (
        top === null ||
        v > top.value ||
        (v === top.value &&
          (rankOf(teamId) < rankOf(top.teamId) ||
            (rankOf(teamId) === rankOf(top.teamId) && teamId < top.teamId)))
      )
        top = { value: v, teamId, when: when(o) };
    }
    return top;
  };
  const highWeek = holder(
    (o) => o.week,
    (o) => periodLabel(o.weekAt),
  );
  const blowout = holder(
    (o) => o.margin,
    (o) => periodLabel(o.marginAt),
  );
  const streak = holder(
    (o) => o.streak,
    (o) =>
      o.streakFrom === o.streakTo
        ? periodLabel(o.streakTo)
        : `${periodLabel(o.streakFrom)} to ${periodLabel(o.streakTo)}`,
  );
  let points: Best = null;
  for (const s of [...facts.standings].sort((x, y) => x.rank - y.rank))
    if (s.pointsFor > 0 && (points === null || s.pointsFor > points.value))
      points = { value: s.pointsFor, teamId: s.teamId, when: 'the regular season' };

  // THE PAST: every season the history kept. A number a season did not keep
  // is 0 there, and holds no record, as in the record book.
  type Past = { value: number; teamId: string; season: number } | null;
  const pastBest = (pick: (t: SeasonFacts['history'][number]['teams'][number]) => number): Past => {
    let top: Past = null;
    for (const p of facts.history)
      for (const t of p.teams) {
        const v = pick(t);
        if (v > 0 && (top === null || v > top.value))
          top = { value: v, teamId: t.teamId, season: p.season };
      }
    return top;
  };
  const RECORDS: {
    id: YearbookRecordId;
    name: string;
    now: Best;
    past: Past;
    say: (v: number) => string;
    /** Finer than `say`, for two figures `say` gives alike. */
    finer: (v: number) => string;
    told: (team: string, v: string, when: string) => string;
  }[] = [
    {
      id: 'high-week',
      name: 'Highest week',
      now: highWeek,
      past: pastBest((t) => t.bestWeek),
      say: pts,
      finer: (v) => String(Number(v.toFixed(3))),
      told: (team, v, when) => `${team} scored ${v} in ${when}.`,
    },
    {
      id: 'blowout',
      name: 'Biggest blowout',
      now: blowout,
      past: pastBest((t) => t.biggestMargin),
      say: pts,
      finer: (v) => String(Number(v.toFixed(3))),
      told: (team, v, when) => `${team} won by ${v} in ${when}.`,
    },
    {
      id: 'streak',
      name: 'Longest win streak',
      now: streak,
      past: pastBest((t) => t.longestStreak),
      say: (v) => String(v),
      finer: (v) => String(v),
      told: (team, v, when) => `${team} won ${v} straight, ${when}.`,
    },
    {
      id: 'season-points',
      name: 'Most points in a season',
      now: points,
      past: pastBest((t) => t.pointsFor),
      say: pts,
      finer: (v) => String(Number(v.toFixed(3))),
      told: (team, v) => `${team} scored ${v} in the regular season.`,
    },
  ];
  const first = RECORDS.every((r) => r.past === null);
  const set: YearbookRecord[] = [];
  for (const r of RECORDS) {
    if (r.now === null) continue;
    // ONE WIN IS NOT A STREAK: a season where nobody won two running has no
    // streak to tell.
    if (r.id === 'streak' && r.now.value < 2) continue;
    // A TIE KEEPS THE OLD HOLDER: a record is set by beating it.
    if (r.past !== null && r.now.value <= r.past.value) continue;
    const value = r.say(r.now.value);
    // TWO FIGURES THAT ROUND ALIKE ARE TOLD FINER, so a record beaten by a
    // few hundredths does not read as a tie.
    const fine = r.past !== null && r.say(r.past.value) === value;
    const sayNow = fine ? r.finer(r.now.value) : value;
    const told = r.told(nameOf(r.now.teamId), sayNow, r.now.when);
    let line = told;
    if (r.past !== null) {
      const was = fine ? r.finer(r.past.value) : r.say(r.past.value);
      // A SEAT THE LEAGUE NO LONGER HAS is a record without a name, as the
      // record book leaves it.
      const holder = facts.teams.some((t) => t.teamId === r.past!.teamId)
        ? `${nameOf(r.past.teamId)}, `
        : '';
      line = `${told} The old record: ${holder}${was}, ${r.past.season}.`;
    }
    set.push({
      id: r.id,
      name: r.name,
      value,
      teamId: r.now.teamId,
      when: r.now.when,
      line,
    });
  }
  const records = {
    first,
    line:
      set.length === 0
        ? first
          ? 'No league record was set this season.'
          : 'No league record fell this season.'
        : first
          ? 'The league’s first season in the book, so its bests are the records.'
          : set.length === 1
            ? 'A league record was set this season.'
            : `${set.length} league records were set this season.`,
    set,
  };

  // ── The games to remember ─────────────────────────────────────────────
  // THE REGULAR SEASON AND THE PLAYOFFS: a consolation game is the toilet
  // bowl's, and stays the awards'. Never the same game twice.
  const pool = games.filter((m) => m.kind !== 'consolation');
  const used = new Set<string>();
  const pick = (candidates: MatchupFact[], score: (m: MatchupFact) => number) =>
    candidates
      .filter((m) => !used.has(m.id))
      .sort((x, y) => score(y) - score(x) || y.period - x.period)[0];
  const gameOf = (
    id: YearbookGameId,
    title: string,
    m: MatchupFact,
    value: string | null,
    line: string,
  ): YearbookGame => {
    used.add(m.id);
    return {
      id,
      title,
      value,
      line,
      matchupId: m.id,
      period: m.period,
      homeTeamId: m.homeTeamId,
      awayTeamId: m.awayTeamId,
      homeScore: m.homeScore,
      awayScore: m.awayScore,
      winnerTeamId: winnerOf(m),
    };
  };
  const beat = (m: MatchupFact) => {
    const w = winnerOf(m)!;
    const home = w === m.homeTeamId;
    return {
      winner: nameOf(w),
      loser: nameOf(home ? m.awayTeamId : m.homeTeamId),
      ws: home ? m.homeScore : m.awayScore,
      ls: home ? m.awayScore : m.homeScore,
    };
  };
  const out: YearbookGame[] = [];
  const closest = pick(
    pool.filter((m) => m.homeScore > 0 && m.awayScore > 0),
    (m) => -Math.abs(m.homeScore - m.awayScore),
  );
  if (closest) {
    // LEVEL IS THE RAW SCORES, NOT THE TENTH THEY ROUND TO: a yard is 0.04
    // of a point, so a game won by one said "finished level" and "0.0". A
    // margin under a tenth is told as it is.
    const raw = Math.abs(closest.homeScore - closest.awayScore);
    const fine = raw > 0 && raw < 0.1;
    const places = Number(raw.toFixed(2)) === Number(raw.toFixed(3)) ? 2 : 3;
    const say = (n: number) => (fine ? n.toFixed(places) : pts(n));
    const margin = fine ? raw.toFixed(places) : pts(round1(raw));
    const label = periodLabel(closest.period);
    let line: string;
    if (winnerOf(closest) === null)
      line = `${label}: ${nameOf(closest.homeTeamId)} and ${nameOf(closest.awayTeamId)} tied at ${pts(closest.homeScore)}.`;
    else {
      const g = beat(closest);
      line =
        raw === 0
          ? `${label}: ${g.winner} and ${g.loser} finished level at ${pts(g.ws)}, and it went to ${g.winner}.`
          : `${label}: ${g.winner} beat ${g.loser} by ${margin}, ${say(g.ws)} to ${say(g.ls)}.`;
    }
    out.push(gameOf('closest', 'The closest game', closest, margin, line));
  }
  const highest = pick(pool, (m) => m.homeScore + m.awayScore);
  if (highest && highest.homeScore + highest.awayScore > 0) {
    const total = round1(highest.homeScore + highest.awayScore);
    out.push(
      gameOf(
        'highest',
        'The highest-scoring game',
        highest,
        pts(total),
        `${periodLabel(highest.period)}: ${nameOf(highest.homeTeamId)} ${pts(highest.homeScore)}, ${nameOf(highest.awayTeamId)} ${pts(highest.awayScore)}, ${pts(total)} between them.`,
      ),
    );
  }
  const blowoutGame = pick(
    pool.filter((m) => winnerOf(m) !== null && m.homeScore !== m.awayScore),
    (m) => Math.abs(m.homeScore - m.awayScore),
  );
  if (blowoutGame) {
    const g = beat(blowoutGame);
    const margin = round1(g.ws - g.ls);
    out.push(
      gameOf(
        'blowout',
        'The biggest blowout',
        blowoutGame,
        pts(margin),
        `${periodLabel(blowoutGame.period)}: ${g.winner} beat ${g.loser} by ${pts(margin)}, ${pts(g.ws)} to ${pts(g.ls)}.`,
      ),
    );
  }
  // THE UPSET: a win over a team with the better record at kickoff, as the
  // facts say it (`opponentRecordBetter`); the most striking is the one whose
  // loser finished highest in the table above its winner.
  const upsets = pool.filter((m) => {
    const w = winnerOf(m);
    if (w === null) return false;
    const l = w === m.homeTeamId ? m.awayTeamId : m.homeTeamId;
    // This game's, in a week of two (Q6): the winner's game against this loser.
    return finals
      .find((p) => p.period === m.period)
      ?.teams.some(
        (t) =>
          t.teamId === w &&
          gamesOf(t).some((g) => g.opponentTeamId === l && g.opponentRecordBetter),
      );
  });
  const upset = pick(upsets, (m) => {
    const w = winnerOf(m)!;
    const l = w === m.homeTeamId ? m.awayTeamId : m.homeTeamId;
    const gap = rankOf(w) - rankOf(l);
    return Number.isFinite(gap) ? gap : 0;
  });
  if (upset) {
    const g = beat(upset);
    out.push(
      gameOf(
        'upset',
        'The upset',
        upset,
        null,
        `${periodLabel(upset.period)}: ${g.winner} beat ${g.loser}, who came in with the better record, ${pts(g.ws)} to ${pts(g.ls)}.`,
      ),
    );
  }

  // ── The moves ──────────────────────────────────────────────────────────
  const arrivals = finals.flatMap((p) => p.transactions);
  const pickups = arrivals.filter((x) => x.kind === 'add' || x.kind === 'claim');
  const traded = arrivals.filter((x) => x.kind === 'trade');
  const most = (xs: typeof arrivals) =>
    xs.reduce<(typeof arrivals)[number] | null>(
      (top, x) => (top === null || x.pointsSince > top.pointsSince ? x : top),
      null,
    );
  const items: YearbookMove[] = [];
  const topPickup = most(pickups);
  if (topPickup !== null && topPickup.pointsSince > 0)
    items.push({
      id: 'pickup',
      title: 'The pickup of the year',
      value: pts(topPickup.pointsSince),
      line: `${facts.playerName(topPickup.playerId)} came ${
        topPickup.kind === 'claim' ? 'off waivers' : 'in as a free agent'
      } to ${nameOf(topPickup.teamId)} in ${periodLabel(topPickup.period)} and scored ${pts(topPickup.pointsSince)} for them.`,
      teamId: topPickup.teamId,
      playerId: topPickup.playerId,
    });
  const topTrade = most(traded);
  if (topTrade !== null && topTrade.pointsSince > 0)
    items.push({
      id: 'trade',
      title: 'The trade of the year',
      value: pts(topTrade.pointsSince),
      line: `${facts.playerName(topTrade.playerId)} came to ${nameOf(topTrade.teamId)} in a trade in ${periodLabel(topTrade.period)} and scored ${pts(topTrade.pointsSince)} for them.`,
      teamId: topTrade.teamId,
      playerId: topTrade.playerId,
    });
  const said = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const moves = {
    pickups: pickups.length,
    traded: traded.length,
    line:
      pickups.length + traded.length === 0
        ? 'A quiet wire: nobody was picked up or traded.'
        : traded.length === 0
          ? `${said(pickups.length, 'player', 'players')} picked up, and no trades.`
          : pickups.length === 0
            ? `Nobody picked up, and ${said(traded.length, 'player', 'players')} moved in trades.`
            : `${said(pickups.length, 'player', 'players')} picked up, and ${said(traded.length, 'player', 'players')} moved in trades.`,
    items,
  };

  // ── The share ──────────────────────────────────────────────────────────
  const high = set.find((r) => r.id === 'high-week') ?? null;
  const highest1 =
    highWeek === null
      ? null
      : `The highest week: ${pts(highWeek.value)}, ${nameOf(highWeek.teamId)}, ${highWeek.when}${
          high !== null && !first ? ', a league record' : ''
        }.`;
  return {
    over: true,
    champion,
    teams,
    records,
    games: out,
    moves,
    share: {
      kicker: `${facts.season} yearbook`,
      headline: nameOf(championId),
      subline: title,
      line: [champion.line, highest1].filter((x): x is string => x !== null).join(' '),
      tone: 'glory',
    },
  };
}

/**
 * The yearbook with each team id turned into the host's own reference to it
 * (a real league's read and the simulator's both, so they cannot draw it
 * differently). A row naming a team the host cannot find is left out.
 */
export function yearbookWith<T>(y: Yearbook, refOf: (teamId: string) => T | null) {
  const champion = y.champion === null ? null : refOf(y.champion.teamId);
  return {
    over: y.over,
    champion:
      y.champion === null || champion === null
        ? null
        : {
            team: champion,
            runnerUp: y.champion.runnerUpTeamId === null ? null : refOf(y.champion.runnerUpTeamId),
            title: y.champion.title,
            line: y.champion.line,
            matchupId: y.champion.matchupId,
          },
    teams: y.teams.flatMap(({ teamId, ...t }) => {
      const team = refOf(teamId);
      return team === null ? [] : [{ team, ...t }];
    }),
    records: {
      first: y.records.first,
      line: y.records.line,
      set: y.records.set.flatMap(({ teamId, ...r }) => {
        const team = refOf(teamId);
        return team === null ? [] : [{ ...r, team }];
      }),
    },
    games: y.games.flatMap((g) => {
      const home = refOf(g.homeTeamId);
      const away = refOf(g.awayTeamId);
      if (home === null || away === null) return [];
      return [
        {
          id: g.id,
          title: g.title,
          value: g.value,
          line: g.line,
          matchupId: g.matchupId,
          period: g.period,
          home: { team: home, score: g.homeScore, won: g.winnerTeamId === g.homeTeamId },
          away: { team: away, score: g.awayScore, won: g.winnerTeamId === g.awayTeamId },
        },
      ];
    }),
    moves: {
      pickups: y.moves.pickups,
      traded: y.moves.traded,
      line: y.moves.line,
      items: y.moves.items.flatMap(({ teamId, playerId: _playerId, ...m }) => {
        const team = refOf(teamId);
        return team === null ? [] : [{ ...m, team }];
      }),
    },
    share: y.share,
  };
}
